import { createUseStoreWithSelectorDeferred } from "@captions-web/utils/zustand-store";
import { z } from "zod";
import { createStore } from "zustand";
import { combine } from "zustand/middleware";

import { createLocalStorageDef } from "~/lib/local-storage";

import { getConfigStore } from "./config";
import { createStoreGetters } from "./root";

type WorkerType = typeof import("@captions-web/mocks/src/browser").worker;
type HandlersType = typeof import("@captions-web/mocks/src/handlers").handlers;

interface MocksStoreState {
  state: "uninitialized" | "enabled" | "disabled" | "unavailable";
  handlersState: Record<string, boolean>;
  availableHandlers: Record<string, string[]>;
}

const WHITELISTED_HANDLERS: (keyof HandlersType)[] = ["mirageStudio"];

const MOCKS_ENABLED_KEY = "mocksEnabled";

const mocksStorageSchema = z
  .object({
    [MOCKS_ENABLED_KEY]: z.boolean(),
  })
  .and(z.record(z.string(), z.boolean()));

type MocksStorage = z.infer<typeof mocksStorageSchema>;

const mocksStorage = createLocalStorageDef("studio_mocks_state", mocksStorageSchema);

export function createMocksStore() {
  return createStore(
    combine(
      {
        state: "uninitialized",
        handlersState: {},
        availableHandlers: {},
      } as MocksStoreState,
      (set, get) => {
        let worker: WorkerType | undefined;
        let handlers: Partial<HandlersType> | undefined;

        return {
          async init() {
            if (getConfigStore().env.APP_ENV === "prod") {
              set({ state: "unavailable" });
              return;
            }

            try {
              const mocksPackage = await import("@captions-web/mocks/src/browser");
              const handlersPackage = await import("@captions-web/mocks/src/handlers");
              worker = mocksPackage.worker;
              handlers = Object.fromEntries(
                Object.entries(handlersPackage.handlers).filter(([key]) =>
                  WHITELISTED_HANDLERS.includes(key as keyof HandlersType)
                )
              );

              const availableHandlers = Object.fromEntries(
                Object.entries(handlers).map(([key, value]) => [
                  key,
                  value.map((handler) => handler.info.header),
                ])
              );

              const { [MOCKS_ENABLED_KEY]: isMockingEnabled, ...handlersState } =
                mocksStorage.get() ?? {};

              if (isMockingEnabled) {
                await worker.start();
                const enabledHandlers = Object.values(handlers)
                  .flat()
                  .filter(
                    (handler) => !!(handlersState as Record<string, boolean>)?.[handler.info.header]
                  );
                worker.use(...enabledHandlers);
              }

              set({
                state: isMockingEnabled ? "enabled" : "disabled",
                handlersState: handlersState ?? {},
                availableHandlers,
              });
            } catch (error) {
              console.error("Failed to initialize mocks:", error);
            }
          },

          async setMockingEnabledGlobally(enabled: boolean) {
            const targetState = enabled ? "enabled" : "disabled";
            if (!worker || get().state === targetState) {
              return;
            }

            if (enabled) {
              await worker.start();
            } else {
              worker.stop();
            }

            const newState: MocksStorage = { ...get().handlersState, [MOCKS_ENABLED_KEY]: enabled };
            mocksStorage.set(newState);
            set({ state: targetState, handlersState: newState });
          },

          async setMockEnabled(name: string, enabled: boolean) {
            if (!worker || !handlers) {
              return;
            }

            if (enabled) {
              const handler = Object.values(handlers)
                .flat()
                .find((handler) => handler.info.header === name);
              if (!handler) {
                console.error(`Could not find handler ${name}`);
                return;
              }
              worker.use(handler);
            } else {
              const currentHandlers = worker.listHandlers();
              const newHandlers = currentHandlers.filter((handler) => handler.info.header !== name);
              worker.resetHandlers(...newHandlers);
            }

            const newState: MocksStorage = {
              ...get().handlersState,
              [name]: enabled,
              [MOCKS_ENABLED_KEY]: true,
            };
            mocksStorage.set(newState);
            set({ handlersState: newState });
          },

          async setAllMocksEnabled(enabled: boolean) {
            if (!worker || !handlers) {
              return;
            }

            if (enabled) {
              const allHandlers = Object.values(handlers).flat();
              worker.use(...allHandlers);
              if (get().state !== "enabled") {
                await worker.start();
              }
              const newState: MocksStorage = {
                ...Object.fromEntries(allHandlers.map((handler) => [handler.info.header, true])),
                [MOCKS_ENABLED_KEY]: true,
              };
              mocksStorage.set(newState);
              set({ state: "enabled", handlersState: newState });
            } else {
              worker.resetHandlers();
              if (get().state !== "disabled") {
                worker.stop();
              }
              const newState: MocksStorage = { [MOCKS_ENABLED_KEY]: false };
              mocksStorage.set(newState);
              set({ state: "disabled", handlersState: newState });
            }
          },
        };
      }
    )
  );
}

type MocksStore = ReturnType<ReturnType<typeof createMocksStore>["getState"]>;

export const {
  getStore: getMocksStore,
  getStoreAsync: getMocksStoreAsync,
  getStoreApi: getMocksStoreApi,
} = createStoreGetters<MocksStore>("mocks");

export const useMocksStore = createUseStoreWithSelectorDeferred(() => getMocksStoreApi());
